import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Calendar, DollarSign } from 'lucide-react';
import { format } from 'date-fns';

interface CampaignManagerProps {
  teamId?: string;
  viewAll?: boolean;
}

const statusColors: Record<string, string> = {
  draft: 'bg-muted text-muted-foreground',
  active: 'bg-green-100 text-green-700',
  paused: 'bg-yellow-100 text-yellow-700',
  completed: 'bg-blue-100 text-blue-700',
};

export function CampaignManager({ teamId, viewAll = false }: CampaignManagerProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', campaign_type: '', start_date: '', end_date: '', budget: '' });

  const { data: campaigns = [], isLoading } = useQuery({
    queryKey: ['marketing-campaigns', teamId, viewAll],
    queryFn: async () => {
      let query = supabase.from('marketing_campaigns').select('*').order('created_at', { ascending: false });
      if (teamId && !viewAll) query = query.eq('team_id', teamId);
      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    },
  });

  const createCampaign = useMutation({
    mutationFn: async () => {
      if (!user) return;
      const { error } = await supabase.from('marketing_campaigns').insert({
        created_by: user.id,
        team_id: teamId || null,
        name: form.name,
        description: form.description || null,
        campaign_type: form.campaign_type || null,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        budget: form.budget ? Number(form.budget) : null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marketing-campaigns'] });
      setShowCreate(false);
      setForm({ name: '', description: '', campaign_type: '', start_date: '', end_date: '', budget: '' });
      toast.success('Campaign created');
    },
    onError: () => toast.error('Failed to create campaign'),
  });

  const updateStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { error } = await supabase.from('marketing_campaigns').update({ status: status as any }).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['marketing-campaigns'] }),
  });

  if (isLoading) return <div className="text-center py-8 text-muted-foreground">Loading campaigns...</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Campaigns</h3>
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild>
            <Button size="sm"><Plus className="h-4 w-4 mr-1" /> New Campaign</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create Campaign</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input placeholder="Campaign name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <Textarea placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              <Input placeholder="Campaign type (e.g. Social Media, Email)" value={form.campaign_type} onChange={(e) => setForm({ ...form, campaign_type: e.target.value })} />
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-muted-foreground">Start</label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
                <div><label className="text-xs text-muted-foreground">End</label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
              </div>
              <Input type="number" placeholder="Budget (ETB)" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} />
              <Button onClick={() => createCampaign.mutate()} disabled={!form.name || createCampaign.isPending} className="w-full">
                {createCampaign.isPending ? 'Creating...' : 'Create Campaign'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {campaigns.map((c: any) => (
          <Card key={c.id}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">{c.name}</CardTitle>
                <Badge className={statusColors[c.status]}>{c.status}</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              {c.description && <p className="text-sm text-muted-foreground line-clamp-2">{c.description}</p>}
              {c.campaign_type && <Badge variant="outline" className="text-xs">{c.campaign_type}</Badge>}
              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                {c.start_date && <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{format(new Date(c.start_date), 'MMM d')}</span>}
                {c.budget && <span className="flex items-center gap-1"><DollarSign className="h-3 w-3" />{Number(c.budget).toLocaleString()} ETB</span>}
              </div>
              <Select value={c.status} onValueChange={(v) => updateStatus.mutate({ id: c.id, status: v })}>
                <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="paused">Paused</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </CardContent>
          </Card>
        ))}
        {campaigns.length === 0 && <p className="text-muted-foreground col-span-full text-center py-8">No campaigns yet</p>}
      </div>
    </div>
  );
}
